/**
 * Advanced ILP modelling — the SECOND optimisation engine (Chris, 2026-08-27).
 *
 * Where "NYMR Claude" (bestfit.ts) is a greedy constructor that grows a day one
 * best marginal move at a time, this engine formulates the whole day as a mixed
 * integer program and hands it to a solver, so the selection of workings is
 * optimised globally rather than locally. It is the candidate-trip-selection +
 * passenger-flow MILP from the operations-research literature (Caprara–Fischetti–
 * Toth train timetabling / Brännlund set-packing, with a line-planning profit
 * objective and a multi-commodity passenger layer):
 *
 *   binaries  x_d      run candidate working d
 *   continuous f_{s,d} originating passengers of demand slot s carried out on d
 *              g_{s,d} slot s's return passengers carried home on d
 *
 *   maximise  Σ r_s·f_{s,d}  −  Σ c_d·x_d           (daily contribution)
 *   s.t.  capacity   Σ_out f + Σ_ret g  ≤ cap_d·x_d          (per candidate)
 *         demand     Σ_d f_{s,d} ≤ N_s ; f_{s,d} ≤ N_s·ρ_out (recapture decay)
 *         returns    Σ_d g_{s,d} = Σ_d f_{s,d} ; g ≤ N_s·ρ_in (min-stay eligible)
 *         NR paths   Σ_{d on segment} x_d ≤ budget            (knapsack)
 *         headway    Σ_{d in clique} x_d ≤ 1                  (interval cliques)
 *         crossing   x_i + x_j ≤ 1 for unresolvable meets     (single line)
 *         fleet      Σ_{d live at t} x_d ≤ engines            (concurrency proxy)
 *
 * The model is big-M-free (capacity uses the true cap_d), clique-tight, and
 * solved by YALPS (pure-TS branch & bound). It returns the SAME shape as
 * bestFit — { deps, log, plan } — so it is a drop-in alternative everywhere the
 * season build or the interactive build calls the optimiser.
 *
 * DELIBERATE SIMPLIFICATIONS (logged for the planner, honoured downstream):
 *  - passengers are continuous (fractional, rounded after) — keeps the LP cheap;
 *  - the fleet cap is a concurrency proxy, NOT a full locomotive-diagram routing
 *    (that is NP-hard); the selected day is then set-planned and re-costed by the
 *    same stock/cost model the greedy uses, so the P&L is identical machinery;
 *  - recapture is a linear decay on the per-(slot,train) ceiling, not the greedy's
 *    step recapture.
 * The final timetable is re-scored by the SAME engine (scoreColourPlan / runClasses
 * / costFromDeps) as NYMR Claude, so the two are compared on identical arithmetic.
 */
import { solve, type Model } from 'yalps'
import type { Departure, SetPlan, Slot } from './types'
import { HEADWAY_MIN, runClasses } from './engine'
import { costFromDeps } from './costs'
import { planSets, applySetPlan, setSeats } from './stock'
import { crossingConflicts } from './constraints'
import { groupKeyFor, journeyMin, locoTurnAt } from './network'
import { fmtMin } from './demand'
import { rescueTrains, type FitConfig } from './bestfit'

/** A generated candidate working the MILP may switch on. */
interface Cand {
  id: string
  dir: 'Northbound' | 'Southbound'
  o: number
  d: number
  minutes: number
  arrMin: number
  cap: number
  cost: number
  group: string
  /** paths used per budgeted segment index (for the NR knapsack rows). */
  segPaths: Record<number, number>
}

const GRID = 30            // candidate departure grid, minutes
const TURN_PROXY = 30      // engine turnaround for the concurrency proxy
const MAX_CANDS = 64       // keep the binary count solver-friendly

/** Distinct demand slots for a direction, pax summed by (o,d,time). */
function mergeSlots(slots: Slot[]): Slot[] {
  const by = new Map<string, Slot>()
  for (const s of slots) {
    if (s.paxPerDay <= 0) continue
    const k = `${s.minutes}|${s.o}|${s.d}`
    const cur = by.get(k)
    if (cur) { cur.paxPerDay += s.paxPerDay }
    else by.set(k, { ...s })
  }
  return [...by.values()].sort((a, b) => a.minutes - b.minutes)
}

export function ilpBuild(cfg: FitConfig): { deps: Departure[]; log: string[]; plan: SetPlan } {
  const {
    net, classes, params, objective, rates, dieselLoco, fleet, sets,
    firstMin, lastMin, formation,
  } = cfg
  const log: string[] = []
  const period = (classes.reduce((t, c) => t + c.weight, 0) || 1) > 1 ? 'week' : 'day'

  const northSlots = mergeSlots(classes.flatMap((c) => c.north))
  const southSlots = mergeSlots(classes.flatMap((c) => c.south))
  const slots = [...northSlots, ...southSlots]
  if (slots.length === 0) {
    log.push('⚠ ADVANCED ILP: no demand slots for this day — nothing to optimise.')
    const plan = planSets(net, classes, [], sets, params, objective, rates, dieselLoco, fleet, Number.MAX_SAFE_INTEGER, formation ?? 'peak')
    return { deps: [], log, plan }
  }

  // The measured capacity of the biggest active rake — the seat bound for a
  // candidate; the real per-working seats come back from the set plan.
  const capMax = Math.max(64, ...sets.filter((s) => s.active).map((s) => setSeats(s, formation ?? 'peak')))
  const budgetedSegs = net.segments
    .map((seg, i) => ({ i, seg }))
    .filter((x) => x.seg.pathsPerDay != null)

  // --- Candidate enumeration -------------------------------------------------
  // Every distinct OD present in demand, at a coarse grid across the operating
  // window; the grid points nearest each observed desired time are added too so
  // a well-timed train always exists. Highest-demand ODs first, capped.
  const odDemand = new Map<string, { o: number; d: number; pax: number }>()
  for (const s of slots) {
    const k = `${s.o}>${s.d}`
    const cur = odDemand.get(k)
    if (cur) cur.pax += s.paxPerDay
    else odDemand.set(k, { o: s.o, d: s.d, pax: s.paxPerDay })
  }
  const ods = [...odDemand.values()].sort((a, b) => b.pax - a.pax)

  const cands: Cand[] = []
  const seenCand = new Set<string>()
  const gridTimes = (extra: number[]): number[] => {
    const out = new Set<number>()
    for (let m = Math.ceil(firstMin / GRID) * GRID; m <= lastMin; m += GRID) out.add(m)
    for (const e of extra) { const g = Math.round(e / GRID) * GRID; if (g >= firstMin && g <= lastMin) out.add(g) }
    return [...out].sort((a, b) => a - b)
  }
  for (const od of ods) {
    if (cands.length >= MAX_CANDS) break
    const dir: Cand['dir'] = od.d > od.o ? 'Northbound' : 'Southbound'
    const desired = slots.filter((s) => s.o === od.o && s.d === od.d).map((s) => s.minutes)
    const jm = journeyMin(net, od.o, od.d)
    const segPaths: Record<number, number> = {}
    for (const { i, seg } of budgetedSegs) {
      const lo = Math.min(od.o, od.d), hi = Math.max(od.o, od.d)
      if (seg.from >= lo && seg.to <= hi) segPaths[i] = seg.pathsPerTraversal ?? 1
    }
    for (const m of gridTimes(desired)) {
      if (cands.length >= MAX_CANDS) break
      const key = `${m}|${od.o}|${od.d}`
      if (seenCand.has(key)) continue
      seenCand.add(key)
      const group = groupKeyFor(net, od.o, od.d)
      const probe: Departure = { id: 'probe', direction: dir, time: fmtMin(m), minutes: m, enabled: true, coaches: 7, o: od.o, d: od.d, arrMin: m + jm, group }
      const cost = costFromDeps(net, [probe], rates, dieselLoco, fleet).marginal
      cands.push({ id: `ilp-${dir[0]}-${fmtMin(m)}-${od.o}-${od.d}`, dir, o: od.o, d: od.d, minutes: m, arrMin: m + jm, cap: capMax, cost, group, segPaths })
    }
  }
  if (cands.length === 0) {
    log.push('⚠ ADVANCED ILP: no feasible candidate workings inside the operating window.')
    const plan = planSets(net, classes, [], sets, params, objective, rates, dieselLoco, fleet, Number.MAX_SAFE_INTEGER, formation ?? 'peak')
    return { deps: [], log, plan }
  }

  // --- Compatibility (which candidate can carry which slot) ------------------
  const covers = (c: Cand, s: Slot) =>
    Math.sign(c.d - c.o) === Math.sign(s.d - s.o) &&
    Math.min(c.o, c.d) <= Math.min(s.o, s.d) && Math.max(c.o, c.d) >= Math.max(s.o, s.d)
  const tol = params.toleranceMin
  const rec = params.recapture ?? 0.7
  const rhoOut = (c: Cand, s: Slot): number => {
    if (!covers(c, s)) return 0
    const dt = Math.abs(c.minutes - s.minutes)
    if (dt > tol) return 0
    return Math.max(rec, 1 - (dt / Math.max(1, tol)) * (1 - rec))
  }
  const dwell = params.opts?.maxStay ? 15 : (params.dwellMin ?? 180)
  const rhoIn = (c: Cand, s: Slot): number => {
    // the return of slot s runs the opposite OD and departs after the stay
    if (Math.sign(c.d - c.o) === Math.sign(s.d - s.o)) return 0
    if (!(Math.min(c.o, c.d) <= Math.min(s.o, s.d) && Math.max(c.o, c.d) >= Math.max(s.o, s.d))) return 0
    const arrival = s.minutes + journeyMin(net, s.o, s.d)
    return c.minutes >= arrival + dwell ? 1 : 0
  }

  // --- Build the YALPS model -------------------------------------------------
  const constraints: Record<string, { max?: number; min?: number; equal?: number }> = {}
  const variables: Record<string, Record<string, number>> = {}
  const binaries: string[] = []

  // capacity rows (one per candidate): Σ f + Σ g − cap·x ≤ 0
  for (const c of cands) constraints[`cap_${c.id}`] = { max: 0 }
  // demand ceilings: Σ_d f_{s,d} ≤ N_s
  slots.forEach((s, si) => { constraints[`dem_${si}`] = { max: s.paxPerDay } })
  // return coverage: Σ g − Σ f = 0 (nobody carried out is left un-returned)
  slots.forEach((_, si) => { constraints[`ret_${si}`] = { equal: 0 } })
  // NR path budget per budgeted segment
  for (const { i, seg } of budgetedSegs) constraints[`nr_${i}`] = { max: seg.pathsPerDay as number }

  // x vars carry: objective (−cost), capacity (−cap), NR (paths)
  for (const c of cands) {
    const v: Record<string, number> = { profit: -c.cost, [`cap_${c.id}`]: -c.cap }
    for (const [i, p] of Object.entries(c.segPaths)) v[`nr_${i}`] = p
    variables[c.id] = v
    binaries.push(c.id)
  }
  // f and g vars
  slots.forEach((s, si) => {
    for (const c of cands) {
      const ro = rhoOut(c, s)
      if (ro >= 0.12) {
        const name = `f_${si}_${c.id}`
        variables[name] = { profit: s.netPerPax, [`cap_${c.id}`]: 1, [`dem_${si}`]: 1, [`ret_${si}`]: -1 }
        constraints[`fcap_${name}`] = { max: s.paxPerDay * ro }   // recapture ceiling
        variables[name][`fcap_${name}`] = 1
      }
      const ri = rhoIn(c, s)
      if (ri > 0) {
        const name = `g_${si}_${c.id}`
        variables[name] = { profit: 0, [`cap_${c.id}`]: 1, [`ret_${si}`]: 1 }
      }
    }
  })

  // headway cliques: per (direction, origin) sort by time, sliding window <HEADWAY
  const byDirO = new Map<string, Cand[]>()
  for (const c of cands) {
    const k = `${c.dir}|${c.o}`
    ;(byDirO.get(k) ?? byDirO.set(k, []).get(k)!).push(c)
  }
  let cliqueN = 0
  for (const group of byDirO.values()) {
    const g = group.sort((a, b) => a.minutes - b.minutes)
    for (let i = 0; i < g.length; i++) {
      const clique = [g[i]]
      for (let j = i + 1; j < g.length && g[j].minutes - g[i].minutes < HEADWAY_MIN; j++) clique.push(g[j])
      if (clique.length > 1) {
        const cn = `hw_${cliqueN++}`
        constraints[cn] = { max: 1 }
        for (const c of clique) variables[c.id][cn] = 1
      }
    }
  }

  // crossing conflicts: unresolvable meets ⇒ at most one of the pair
  let crossN = 0
  for (let i = 0; i < cands.length; i++) {
    for (let j = i + 1; j < cands.length; j++) {
      const a = cands[i], b = cands[j]
      // only opposite directions can meet head-on; same-dir handled by headway
      if (Math.sign(a.d - a.o) === Math.sign(b.d - b.o)) continue
      if (Math.abs(a.minutes - b.minutes) > 240) continue   // cannot meet — prune
      const legA: Departure = { id: a.id, direction: a.dir, time: fmtMin(a.minutes), minutes: a.minutes, enabled: true, coaches: 7, o: a.o, d: a.d, arrMin: a.arrMin }
      const legB: Departure = { id: b.id, direction: b.dir, time: fmtMin(b.minutes), minutes: b.minutes, enabled: true, coaches: 7, o: b.o, d: b.d, arrMin: b.arrMin }
      if (crossingConflicts(net, [legA, legB]).some((x) => !x.resolvable)) {
        const cn = `x_${crossN++}`
        constraints[cn] = { max: 1 }
        variables[a.id][cn] = 1
        variables[b.id][cn] = 1
      }
    }
  }

  // fleet concurrency proxy: at each grid slice, live engines ≤ fleet size
  const engines = fleet.maxSteam + fleet.maxDiesel + fleet.maxRailcar
  let slotN = 0
  for (let t = firstMin; t <= lastMin + TURN_PROXY; t += GRID) {
    const live = cands.filter((c) => c.minutes <= t && (c.arrMin + locoTurnAt(net, c.d)) >= t)
    if (live.length > engines) {
      const cn = `fl_${slotN++}`
      constraints[cn] = { max: engines }
      for (const c of live) variables[c.id][cn] = 1
    }
  }

  const model: Model = { direction: 'maximize', objective: 'profit', constraints, variables, binaries }
  const res = solve(model, { timeout: 4000, tolerance: 0.01 })

  const nVars = Object.keys(variables).length
  const nRows = Object.keys(constraints).length
  log.push(`Advanced ILP: ${cands.length} candidate workings (${binaries.length} binaries), ${nVars} variables, ${nRows} constraints, ${crossN} crossing + ${cliqueN} headway cliques.`)

  if (res.status !== 'optimal' && res.status !== 'timedout') {
    log.push(`⚠ ADVANCED ILP could not solve (${res.status}) — the constraints admit no timetable inside the window, path budget and fleet. Nothing selected; an empty timetable is not an answer.`)
    const plan = planSets(net, classes, [], sets, params, objective, rates, dieselLoco, fleet, Number.MAX_SAFE_INTEGER, formation ?? 'peak')
    return { deps: [], log, plan }
  }
  if (res.status === 'timedout') log.push('Advanced ILP hit its 4s time limit — returning the best timetable found so far (near-optimal, within the 1% gap).')

  // --- Extract the selected workings ----------------------------------------
  const candIds = new Set(cands.map((c) => c.id))
  const chosen = new Set<string>()
  for (const [name, val] of res.variables) if (candIds.has(name) && val > 0.5) chosen.add(name)
  const picked = cands.filter((c) => chosen.has(c.id)).sort((a, b) => a.minutes - b.minutes)

  if (picked.length === 0) {
    log.push(`⚠ ADVANCED ILP ADDED NOTHING: under this cost model no working's fares beat its marginal cost (contribution ${res.result <= 0 ? '≤ 0' : res.result.toFixed(0)}). Check the cost model and the demand — an empty timetable is not an answer.`)
    const plan = planSets(net, classes, [], sets, params, objective, rates, dieselLoco, fleet, Number.MAX_SAFE_INTEGER, formation ?? 'peak')
    return { deps: [], log, plan }
  }

  const maxCoachFor = (group: string): number => {
    const eligible = sets.filter((s) => s.active && (!s.group || s.group === group))
    return eligible.length > 0 ? Math.max(...eligible.map((s) => s.coaches)) : 2
  }
  const built: Departure[] = picked.map((c) => ({
    id: c.id, direction: c.dir, time: fmtMin(c.minutes), minutes: c.minutes, enabled: true,
    coaches: maxCoachFor(c.group), o: c.o, d: c.d, arrMin: c.arrMin, group: c.group,
  }))
  log.push(`Advanced ILP selected ${built.length} workings for the ${period} (model contribution ${res.result.toFixed(0)} at marginal cost).`)

  // The ILP does the GLOBAL selection; the "no unreturned" contract is then
  // guaranteed by the SAME rescue the greedy uses (its return-assignment differs
  // from the model's optimistic booking, so a rescue top-up brings everyone home
  // and rebalances stock). Then set-plan and re-check under the REAL rakes.
  const strandedLimit = Math.max(0.5, cfg.maxUnreturned)
  let coreDeps = rescueTrains(cfg, built, true, strandedLimit)
  log.push(...coreDeps.log)
  let plan = planSets(net, classes, coreDeps.deps, sets, params, objective, rates, dieselLoco, fleet, Number.MAX_SAFE_INTEGER, formation ?? 'peak')
  let deps = applySetPlan(coreDeps.deps, plan)
  for (let round = 0; round < 3; round++) {
    if (runClasses(net, classes, deps, params).unreturned <= strandedLimit) break
    coreDeps = rescueTrains(cfg, coreDeps.deps, true, strandedLimit)
    log.push(...coreDeps.log)
    plan = planSets(net, classes, coreDeps.deps, sets, params, objective, rates, dieselLoco, fleet, Number.MAX_SAFE_INTEGER, formation ?? 'peak')
    deps = applySetPlan(coreDeps.deps, plan)
  }

  const svc = deps.filter((d) => d.enabled && !d.positioning).length
  const finalR = runClasses(net, classes, deps, params)
  log.push(`Advanced ILP finished: ${svc} revenue working${svc === 1 ? '' : 's'}, ${Math.round(finalR.unreturned)} unreturned under the assigned rakes (allowance ${Math.round(cfg.maxUnreturned)}). Re-scored exactly by the shared engine downstream.`)
  for (const warn of plan.warnings) log.push(`⚠ ${warn}`)
  return { deps, log, plan }
}
